import React, { useEffect, useRef, useState } from "react";
import {
  Plus,
  FileText,
  Loader2,
  ArrowLeft,
  Menu,
  ListTree,
  Type,
  Hash,
  Search,
  UploadCloud,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
// import { CKEditor } from "@ckeditor/ckeditor5-react";
// import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

import { CKEditor } from "@ckeditor/ckeditor5-react";
import {
  ClassicEditor,
  Essentials,
  Paragraph,
  Heading,
  Bold,
  Italic,
  Underline,
  Font,
  Alignment,
  List,
  Link,
  BlockQuote,
  Table as CKTable,
  TableToolbar,
  Image as CKImage,
  ImageUpload,
  ImageToolbar,
  ImageStyle,
  ImageResize,
  SimpleUploadAdapter,
  HorizontalLine,
} from "ckeditor5";
import "ckeditor5/ckeditor5.css";

import adminApi from "./adminApi";

const SEQUENCES = Array.from({ length: 31 }, (_, i) => i + 1);

const emptyForm = {
  menu_pageid: "",
  parent_pageid: "",
  page_name: "",
  seq_id: "",
  page_title: "",
  page_contents: "",
  seo_metatag: "",
  seo_metatitle: "",
  seo_metadesc: "",
  seo_keyword: "",
  ischeckedpdf: false,
};

export default function AddSubSubMenu() {
  const navigate = useNavigate();

  const [form, setForm] = useState(emptyForm);

  const [menus, setMenus] = useState([]);
  const [submenus, setSubmenus] = useState([]);

  const [file, setFile] = useState(null);
  const [filePreviewName, setFilePreviewName] = useState("");

  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  // NEW: sequence loading state (per-submenu next-sequence lookup)
  const [seqLoading, setSeqLoading] = useState(false);

  const fileInputRef = useRef(null);

  // =========================================================
  // FETCH MENUS
  // =========================================================

  const fetchMenus = async () => {
    try {
      const res = await adminApi.get("/admin/sub-sub-menu/meta/menus");
      setMenus(res.data.menus || []);
    } catch (err) {
      console.error("Could not load menu list:", err);

      setFormError(
        err.response?.data?.message || "Could not load menu list."
      );
    }
  };

  // =========================================================
  // FETCH SUB MENUS
  // =========================================================

  const fetchSubmenusFor = async (menuPageId) => {
    if (!menuPageId) {
      setSubmenus([]);
      return;
    }

    try {
      const res = await adminApi.get(
        "/admin/sub-sub-menu/meta/submenus",
        {
          params: {
            parent: menuPageId,
          },
        }
      );

      setSubmenus(res.data.submenus || []);
    } catch (err) {
      console.error("Could not load submenu list:", err);

      setFormError(
        err.response?.data?.message || "Could not load submenu list."
      );
    }
  };

  useEffect(() => {
    fetchMenus();
  }, []);

  // =========================================================
  // MENU CHANGE
  // =========================================================
  // Changing the Menu resets Sub Menu + Sequence, since the sequence
  // is scoped to whichever Sub Menu ends up selected next.

  const handleMenuChange = (menuPageId) => {
    setForm((prev) => ({
      ...prev,
      menu_pageid: menuPageId,
      parent_pageid: "",
      seq_id: "",
    }));

    fetchSubmenusFor(menuPageId);
  };

  // =========================================================
  // SUB MENU CHANGE -> NEXT SEQUENCE FOR THAT SUB MENU
  // =========================================================
  // Sequence is scoped to the sub-sub-menus that already exist under the
  // chosen Sub Menu (parent_pageid), not globally and not per Menu.

  const handleSubmenuChange = async (parentPageId) => {
    setForm((prev) => ({
      ...prev,
      parent_pageid: parentPageId,
    }));

    if (!parentPageId) {
      setForm((prev) => ({ ...prev, seq_id: "" }));
      return;
    }

    setSeqLoading(true);

    try {
      const res = await adminApi.get("/admin/sub-sub-menu", {
        params: { parent: parentPageId },
      });

      const items = res.data.items || [];

      const maxSequence = items.reduce((max, item) => {
        const sequence = Number(item.seq_id) || 0;
        return Math.max(max, sequence);
      }, 0);

      setForm((prev) => ({
        ...prev,
        seq_id: maxSequence + 1,
      }));
    } catch (err) {
      console.error("Could not get next sequence for this sub menu:", err);

      setForm((prev) => ({
        ...prev,
        seq_id: 1,
      }));
    } finally {
      setSeqLoading(false);
    }
  };

  // =========================================================
  // FILE CHANGE
  // =========================================================

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];

    if (!selectedFile) return;

    if (!selectedFile.name.toLowerCase().endsWith(".pdf")) {
      setFormError("Only PDF files are allowed.");
      return;
    }

    if (selectedFile.size > 4 * 1024 * 1024) {
      setFormError("PDF file size must be less than 4MB.");
      return;
    }

    setFormError("");

    setFile(selectedFile);
    setFilePreviewName(selectedFile.name);
  };

  const removeFile = () => {
    setFile(null);
    setFilePreviewName("");

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // =========================================================
  // SUBMIT
  // =========================================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    setFormError("");

    if (!form.menu_pageid) {
      setFormError("Menu Name is required.");
      return;
    }

    if (!form.parent_pageid) {
      setFormError("Sub Menu Name is required.");
      return;
    }

    if (!form.page_name.trim()) {
      setFormError("Sub-sub Menu Name is required.");
      return;
    }

    if (!form.seq_id) {
      setFormError("Sequence is required.");
      return;
    }

    if (form.ischeckedpdf && !file) {
      setFormError(
        "A PDF file is required when 'Add PDF for this menu' is checked."
      );
      return;
    }

    const fd = new FormData();

    fd.append("parent_pageid", form.parent_pageid);
    fd.append("page_name", form.page_name.trim());
    fd.append("seq_id", form.seq_id);
    fd.append("page_title", form.page_title.trim());
    fd.append("page_contents", form.page_contents.trim());

    fd.append("seo_metatag", form.seo_metatag);
    fd.append("seo_metatitle", form.seo_metatitle);
    fd.append("seo_metadesc", form.seo_metadesc);
    fd.append("seo_keyword", form.seo_keyword);

    fd.append(
      "ischeckedpdf",
      form.ischeckedpdf ? "1" : "0"
    );

    if (file) {
      fd.append("attachment", file);
    }

    setSaving(true);

    try {
      await adminApi.post("/admin/sub-sub-menu", fd);
  
      navigate("/admin/contents/sub-sub-menu", {
        state: {
          message: "Sub-sub menu content added successfully.",
        },
      });
    } catch (err) {
      console.error(err);

      setFormError(
        err.response?.data?.message ||
          "Something went wrong. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  // =========================================================
  // INPUT STYLE
  // =========================================================

  const inputClass =
    "w-full h-[44px] rounded-xl border border-slate-200 bg-white px-3.5 text-sm text-slate-700 outline-none transition-all placeholder:text-slate-400 hover:border-slate-300 focus:border-[#0EA5A4] focus:ring-4 focus:ring-[#0EA5A4]/10";

  const textareaClass =
    "w-full rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-sm text-slate-700 outline-none transition-all placeholder:text-slate-400 hover:border-slate-300 focus:border-[#0EA5A4] focus:ring-4 focus:ring-[#0EA5A4]/10 resize-none";

  return (
    <div className="min-h-full bg-[#f5f7fa]">

      {/* =====================================================
          PAGE HEADER
      ===================================================== */}

      <div className="mb-3">

        <div className="max-w-[1400px] mx-auto">

          <div className="bg-white  border border-slate-200 shadow-[0_2px_12px_rgba(15,23,42,0.04)]">

            {/* <div className="px-5 sm:px-7 py-5"> */}

              <div className="px-2 sm:px-2" style={{paddingTop:"10px",paddingBottom:"10px"}}>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">

                <div className="flex items-center gap-3">

                

                  <div>

                    <h1 className="font-display text-[18px] sm:text-[18px] font-bold text-slate-800">
                      Add Sub-sub Menu Content
                    </h1>

                    <p className="text-xs sm:text-sm text-slate-400 mt-0.5 d-none">
                      Create and manage your sub-sub menu content page
                    </p>

                  </div>

                </div>

                <button
                  type="button"
                  onClick={() =>
                    navigate("/admin/contents/sub-sub-menu")
                  }
                  className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-xl border border-slate-200 bg-white text-sm font-medium text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition"
                >
                  <ArrowLeft size={16} />
                  Back
                </button>

              </div>

            </div>

          </div>

        </div>

      </div>

      {/* =====================================================
          FORM
      ===================================================== */}

      <div className="">

        <div className="max-w-[1400px] mx-auto">

          <form
            onSubmit={handleSubmit}
            className="space-y-5"
          >

            {/* =================================================
                BASIC INFORMATION CARD
            ================================================= */}

            <div className="bg-white  border border-slate-200 shadow-[0_2px_12px_rgba(15,23,42,0.04)] overflow-hidden">

              {/* Card Header */}

              <div className="px-4 sm:px-7 py-4 border-b border-slate-100 bg-gradient-to-r from-slate-50/80 to-white d-none">

                <div className="flex items-center gap-3">

                  <div className="w-9 h-9 rounded-lg bg-[#0B2545]/10 flex items-center justify-center">

                    <Menu
                      size={17}
                      className="text-[#0B2545]"
                    />

                  </div>

                  <div>

                    <h2 className="text-sm font-bold text-slate-700">
                      Menu Information
                    </h2>

                    <p className="text-[11px] text-slate-400 mt-0.5">
                      Select the menu hierarchy and page details
                    </p>

                  </div>

                </div>

              </div>

              {/* Card Body */}

              <div className="p-1 sm:p-7">
<table className="table table-bordered" style={{marginBottom:"1px", tableLayout:"fixed", width:"100%"}}>
  <tbody>
    <tr>
      <td style={{width:"10%"}}>
      <label className="block text-xs font-semibold text-slate-600 labelStly">
        Menu Name <span className="text-red-500">*</span>
      </label>
      </td>
      <td style={{width:"40%"}}>
<div className="relative">

                      <Menu
                        size={16}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                      />

                      <select
                        value={form.menu_pageid}
                        onChange={(e) =>
                          handleMenuChange(e.target.value)
                        }
                        className={`${inputClass} pl-9 appearance-none cursor-pointer`}
                      >

                        <option value="">
                          -- Select Menu --
                        </option>

                        {menus.map((menu) => (
                          <option
                            key={menu.page_id}
                            value={menu.page_id}
                          >
                            {menu.page_name}
                          </option>
                        ))}

                      </select>

                    </div>
      </td>
      <td style={{width:"10%"}}>
       <label className="block text-xs font-semibold text-slate-600 labelStly">
          Sub Menu Name <span className="text-red-500">*</span>
        </label>
      </td>
      <td style={{width:"40%"}}>

                    <div className="relative">

                      <ListTree
                        size={16}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                      />

                      <select
                        value={form.parent_pageid}
                        onChange={(e) =>
                          handleSubmenuChange(e.target.value)
                        }
                        disabled={!form.menu_pageid}
                        className={`${inputClass} pl-9 appearance-none cursor-pointer disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed`}
                      >

                        <option value="">
                          -- Select Sub Menu --
                        </option>

                        {submenus.map((submenu) => (
                          <option
                            key={submenu.page_id}
                            value={submenu.page_id}
                          >
                            {submenu.page_name}
                          </option>
                        ))}

                      </select>

                    </div>
      </td>
      </tr>
      <tr>
      <td style={{width:"10%"}}>
        <label className="block text-xs font-semibold text-slate-600 labelStly">
          Sub-sub Menu Name <span className="text-red-500">*</span>
        </label>
      </td>
      <td style={{width:"40%"}}>
<div className="relative">

                      <Type
                        size={16}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                      />

                      <input
                        type="text"
                        value={form.page_name}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            page_name: e.target.value,
                          }))
                        }
                        placeholder="Enter sub-sub menu name"
                        className={`${inputClass} pl-9`}
                      />

                    </div>
      </td>
      <td style={{width:"10%"}}>
       <label className="block text-xs font-semibold text-slate-600 labelStly">
          Sequence <span className="text-red-500">*</span>
        </label>
      </td>
      <td style={{width:"40%"}}>
<div className="relative">

                      <Hash
                        size={16}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                      />

                      <select
                        value={form.seq_id}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            seq_id: e.target.value,
                          }))
                        }
                        disabled={seqLoading || !form.parent_pageid}
                        className={`${inputClass} pl-9 appearance-none cursor-pointer disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed`}
                      >

                        {seqLoading ? (

                          <option value="">
                            Loading sequence...
                          </option>

                        ) : !form.parent_pageid ? (

                          <option value="">
                            Select a sub menu first
                          </option>

                        ) : (

                          <>
                            <option value="">
                              -- Select Sequence --
                            </option>

                            {SEQUENCES.map((sequence) => (
                              <option
                                key={sequence}
                                value={sequence}
                              >
                                {sequence}
                              </option>
                            ))}

                            {/*
                                If the computed sequence is beyond 31,
                                keep it selectable instead of silently dropping it.
                            */}
                            {Number(form.seq_id) > 31 && (

                              <option value={form.seq_id}>
                                {form.seq_id}
                              </option>

                            )}
                          </>

                        )}

                      </select>

                    </div>
      </td>
      
  </tr>

  </tbody>
</table>
                

              </div>

            </div>

            {/* =================================================
                PAGE CONTENT CARD
            ================================================= */}

            <div className="bg-white  border border-slate-200 shadow-[0_2px_12px_rgba(15,23,42,0.04)] overflow-hidden">

              <div className="px-4 sm:px-7 py-4 border-b border-slate-100 bg-gradient-to-r from-slate-50/80 to-white">

                <div className="flex items-center gap-3">

                  <div className="w-9 h-9 rounded-lg bg-[#0EA5A4]/10 flex items-center justify-center">

                    <FileText
                      size={17}
                      className="text-[#0EA5A4]"
                    />

                  </div>

                  <div>

                    <h2 className="text-sm font-bold text-slate-700">
                      Page Content
                    </h2>

                    <p className="text-[11px] text-slate-400 mt-0.5">
                      Add the content that will appear on this page
                    </p>

                  </div>

                </div>

              </div>

              <div className="p-4 sm:p-7" style={{marginTop:"-30px"}}>
<table className="table table-bordered" style={{marginBottom:"1px"}}>
  <tbody>
     <tr>
      <td style={{width:"10%"}}>
       <label className="block text-xs font-semibold text-slate-600 labelStly">
          Page Title <span className="text-red-500">*</span>
        </label>
      </td>
      <td>
<div className="relative">

                      
                  <div className="lg:col-span-2">

                

                    <input
                      type="text"
                      value={form.page_title}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          page_title: e.target.value,
                        }))
                      }
                      placeholder="Enter page title"
                      className={inputClass}
                    />

                  </div>

                    </div>
      </td>
  </tr>
  <tr>
      <td style={{width:"10%"}}>
        <label className="block text-xs font-semibold text-slate-600 labelStly">
          Page Contents
        </label>
      </td>
      <td>
   <div className="rounded-xl overflow-hidden border border-slate-200">

                  {/* <CKEditor
                    editor={ClassicEditor}
                    data={form.page_contents}
                    onChange={(event, editor) => {
                      const data = editor.getData();

                      setForm((prev) => ({
                        ...prev,
                        page_contents: data,
                      }));
                    }}
                  /> */}

                  <CKEditor
                    editor={ClassicEditor}
                    data={form.page_contents}
                    config={{
                      licenseKey: "GPL",
                      plugins: [
                        Essentials, Paragraph, Heading,
                        Bold, Italic, Underline,
                        Font, Alignment,
                        List, Link, BlockQuote,
                        CKTable, TableToolbar,
                        CKImage, ImageUpload, ImageToolbar, ImageStyle, ImageResize,
                        SimpleUploadAdapter,
                        HorizontalLine,
                      ],
                      toolbar: [
                        "heading", "|",
                        "fontFamily", "fontSize", "|",
                        "bold", "italic", "underline", "|",
                        "fontColor", "fontBackgroundColor", "|",
                        "alignment", "|",
                        "bulletedList", "numberedList", "|",
                        "link", "blockQuote", "insertTable", "uploadImage", "horizontalLine", "|",
                        "undo", "redo",
                      ],
                      fontFamily: {
                        options: [
                          "default",
                          "Arial, Helvetica, sans-serif",
                          "Georgia, serif",
                          "Times New Roman, Times, serif",
                          "Courier New, Courier, monospace",
                          "Verdana, Geneva, sans-serif",
                          "Tahoma, Geneva, sans-serif",
                          "Trebuchet MS, Helvetica, sans-serif",
                        ],
                        supportAllValues: true,
                      },
                      fontSize: {
                        options: [9, 11, 13, "default", 17, 19, 21, 24, 28, 32],
                        supportAllValues: true,
                      },
                      table: {
                        contentToolbar: ["tableColumn", "tableRow", "mergeTableCells"],
                      },
                      image: {
                        toolbar: [
                          "imageStyle:alignLeft",
                          "imageStyle:alignRight",
                          "imageStyle:block",
                          "|",
                          "toggleImageCaption",
                          "imageTextAlternative",
                          "resizeImage",
                        ],
                      },
                      simpleUpload: {
                        uploadUrl: `${adminApi.defaults.baseURL}/admin/sub-sub-menu/upload-image`,
                        headers: {
                          Authorization: `Bearer ${localStorage.getItem("mkcg_admin_token")}`,
                        },
                      },
                    }}
                    onChange={(event, editor) => {
                      const data = editor.getData();
                      setForm((prev) => ({
                        ...prev,
                        page_contents: data,
                      }));
                    }}
                  />

                </div>
      </td>
  </tr>
  </tbody>
</table>
                
                      <div className="mt-4">
             <div className="flex items-center gap-3 mb-4">

                          <div className="h-px bg-slate-200 flex-1" />

                          <div className="flex items-center gap-2 px-3">

                            <Search
                              size={15}
                              className="text-[#0EA5A4]"
                            />

                            <span className="text-xs font-bold uppercase tracking-wider text-[#0EA5A4]">
                              SEO Details
                            </span>

                          </div>

                          <div className="h-px bg-slate-200 flex-1" />

                        </div>
                        </div>

                        <table className="table table-bordered" style={{marginBottom:"1px"}}>
  <tbody>
    <tr>
      <td style={{width:"10%"}}>
        <label className="block text-xs font-semibold text-slate-600  labelStly">
          Meta Tags
        </label>
      </td>
      <td>
<textarea
                      rows={3}
                      value={form.seo_metatag}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          seo_metatag: e.target.value,
                        }))
                      }
                      placeholder="Enter meta tags"
                      className={textareaClass}
                    />
      </td>
       <td style={{width:"10%"}}>
        <label className="block text-xs font-semibold text-slate-600  labelStly">
          Meta Title
        </label>
      </td>
      <td>
  <textarea
                      rows={3}
                      value={form.seo_metatitle}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          seo_metatitle: e.target.value,
                        }))
                      }
                      placeholder="Enter meta title"
                      className={textareaClass}
                    />

      </td>
      </tr>
      <tr>
      <td style={{width:"10%"}}>
        <label className="block text-xs font-semibold text-slate-600 labelStly">
          Meta Description
        </label>
      </td>
      <td>
 <textarea
                      rows={3}
                      value={form.seo_metadesc}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          seo_metadesc: e.target.value,
                        }))
                      }
                      placeholder="Enter meta description"
                      className={textareaClass}
                    />
      </td>
       <td style={{width:"10%"}}>
        <label className="block text-xs font-semibold text-slate-600  labelStly">
          Keywords
        </label>
      </td>
      <td>
 <textarea
                      rows={3}
                      value={form.seo_keyword}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          seo_keyword: e.target.value,
                        }))
                      }
                      placeholder="Enter keywords separated by commas"
                      className={textareaClass}
                    />
      </td>
  </tr>
  </tbody>
</table>

              </div>

            </div>

           
            {/* =================================================
                PDF CARD
            ================================================= */}

            <div className="bg-white  border border-slate-200 shadow-[0_2px_12px_rgba(15,23,42,0.04)] overflow-hidden">

              <div className="px-4 sm:px-7 py-4 border-b border-slate-100 bg-gradient-to-r from-slate-50/80 to-white">

                <div className="flex items-center gap-3">

                  <div className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center">

                    <FileText
                      size={17}
                      className="text-red-500"
                    />

                  </div>
<div style={{display:"flex"}}>
                  <div>

                    <h2 className="text-sm font-bold text-slate-700">
                      PDF Attachment
                    </h2>

                    <p className="text-[11px] text-slate-400 mt-0.5">
                      Optionally attach a PDF document to this menu
                    </p>

                  </div>
                  <div>
                      <label className="flex items-center gap-2.5 cursor-pointer select-none">
                    <span className="hidden sm:block text-xs font-medium text-slate-500">
                      Enable PDF
                    </span>

                    <div className="relative">

                  
                        <input
                    type="checkbox"
                    checked={form.ischeckedpdf}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        ischeckedpdf: e.target.checked,
                      }))
                    }
                    className="sr-only peer"
                  />

                      <div
                        className="
                          w-10 h-5
                          bg-slate-200
                          rounded-full
                          peer
                          peer-checked:bg-[#0EA5A4]
                          transition
                        "
                      />

                      <div
                        className="
                          absolute
                          left-0.5 top-0.5
                          w-4 h-4
                          bg-white
                          rounded-full
                          shadow-sm
                          transition-transform
                          peer-checked:translate-x-5
                        "
                      />

                    </div>

                  </label>
                  </div>
                  </div>
                </div>

              </div>

              <div className="" style={{paddingLeft:"20px"}}>

               
                {/* UPLOAD */}

                {form.ischeckedpdf && (

                  <div className="mb-3">

                    <label className="text-xs font-semibold text-slate-600 mb-2 block">

                      Upload PDF
                      <span className="text-red-500 ml-1">*</span>

                    </label>

                    {!file ? (

                      <div
                        onClick={() =>
                          fileInputRef.current?.click()
                        }
                        className="group border-2 border-dashed border-slate-200  bg-slate-50/60 hover:bg-[#0EA5A4]/[0.03] hover:border-[#0EA5A4]/50 transition cursor-pointer p-2"
                      >

                        <div className="flex flex-col items-center justify-center text-center">

                          <div className="w-14 h-14 rounded-2xl bg-white border border-slate-200 shadow-sm flex items-center justify-center mb-3 group-hover:border-[#0EA5A4]/30 transition">

                            <UploadCloud
                              size={24}
                              className="text-slate-400 group-hover:text-[#0EA5A4] transition"
                            />

                          </div>

                          <p className="text-sm font-semibold text-slate-600">
                            Click to upload PDF
                          </p>

                          <p className="text-xs text-slate-400 mt-1">
                            PDF only · Maximum 4MB
                          </p>

                        </div>

                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".pdf,application/pdf"
                          onChange={handleFileChange}
                          className="hidden"
                        />

                      </div>

                    ) : (

                      <div className="flex items-center justify-between gap-4 border border-emerald-100 bg-emerald-50/60 rounded-2xl p-4">

                        <div className="flex items-center gap-3 min-w-0">

                          <div className="w-11 h-11 rounded-xl bg-white border border-emerald-100 flex items-center justify-center shrink-0">

                            <FileText
                              size={20}
                              className="text-red-500"
                            />

                          </div>

                          <div className="min-w-0">

                            <p className="text-sm font-semibold text-slate-700 truncate">
                              {filePreviewName}
                            </p>

                            <p className="text-[11px] text-emerald-600 mt-0.5">
                              PDF selected successfully
                            </p>

                          </div>

                        </div>

                        <button
                          type="button"
                          onClick={removeFile}
                          className="w-9 h-9 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-400 hover:text-red-500 hover:border-red-200 transition shrink-0"
                        >

                          <X size={16} />

                        </button>

                      </div>

                    )}

                  </div>

                )}

              </div>

            </div>

            {/* =================================================
                ERROR
            ================================================= */}

            {formError && (

              <div className="flex items-start gap-3 px-4 py-3.5 bg-red-50 border border-red-100 rounded-xl">

                <div className="w-5 h-5 rounded-full bg-red-100 text-red-600 flex items-center justify-center text-xs font-bold shrink-0">
                  !
                </div>

                <p className="text-sm text-red-600">
                  {formError}
                </p>

              </div>

            )}

            {/* =================================================
                FOOTER ACTION
            ================================================= */}

            <div className="bg-white  border border-slate-200 shadow-[0_2px_12px_rgba(15,23,42,0.04)]">

              <div className="px-5 sm:px-7 py-4 flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-3">

                <button
                  type="button"
                  onClick={() =>
                    navigate("/admin/contents/sub-sub-menu")
                  }
                  className="h-11 px-6 rounded-xl text-sm font-semibold text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 hover:border-slate-300 transition"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={saving}
                  className="h-11 px-7 rounded-xl flex items-center justify-center gap-2 bg-gradient-to-r from-[#0B2545] to-[#0EA5A4] hover:from-[#143A63] hover:to-[#0c9291] text-white text-sm font-semibold shadow-sm hover:shadow-md transition disabled:opacity-60 disabled:cursor-not-allowed"
                >

                  {saving ? (
                    <Loader2
                      size={17}
                      className="animate-spin"
                    />
                  ) : (
                    <Plus size={17} />
                  )}

                  {saving ? "Saving..." : "Create Content"}

                </button>

              </div>

            </div>

          </form>

        </div>

      </div>

    </div>
  );
}
